import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
  Switch,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { FamilyWeekend } from "@/types";
import { useThemeColors } from "@/providers/ThemeProvider";
import DateTimePicker from "@react-native-community/datetimepicker";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { useAuth } from "@/providers/AuthProvider";
import { logWeekendAction } from "@/lib/activity-log";

/**
 * Input field component
 */
interface InputFieldProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  keyboardType?: "default" | "numeric";
  multiline?: boolean;
  disabled?: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

const InputField = ({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = "default",
  multiline = false,
  disabled = false,
  colors,
}: InputFieldProps) => (
  <View className="mb-4">
    <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
      {label}
    </Text>
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.textTertiary}
      keyboardType={keyboardType}
      multiline={multiline}
      numberOfLines={multiline ? 3 : 1}
      editable={!disabled}
      className={`rounded-xl px-4 py-3 ${multiline ? "h-24" : ""}`}
      style={{
        backgroundColor: disabled ? colors.background : colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        color: disabled ? colors.textSecondary : colors.text,
        textAlignVertical: multiline ? "top" : "center",
      }}
    />
  </View>
);

/**
 * Date picker field component
 */
interface DateFieldProps {
  label: string;
  value: Date | null;
  onDateChange: (date: Date | null) => void;
  placeholder: string;
  colors: ReturnType<typeof useThemeColors>;
}

const DateField = ({ label, value, onDateChange, placeholder, colors }: DateFieldProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(value || new Date());

  const handleChange = (event: any, selectedDate?: Date) => {
    if (Platform.OS === "android") {
      setShowPicker(false);
      if (event.type === "set" && selectedDate) {
        onDateChange(selectedDate);
      }
    } else {
      if (selectedDate) {
        setTempDate(selectedDate);
      }
    }
  };

  const handleConfirmIOS = () => {
    onDateChange(tempDate);
    setShowPicker(false);
  };

  const handleCancelIOS = () => {
    setShowPicker(false);
  };

  const handleClear = () => {
    onDateChange(null);
  };

  const formattedDate = value
    ? format(value, "d MMMM yyyy", { locale: nl })
    : null;

  return (
    <View className="mb-4">
      <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
        {label}
      </Text>
      <Pressable
        onPress={() => setShowPicker(true)}
        className="rounded-xl px-4 py-3 flex-row items-center justify-between"
        style={{
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        <Text style={{ color: formattedDate ? colors.text : colors.textTertiary }}>
          {formattedDate || placeholder}
        </Text>
        <View className="flex-row items-center">
          {value && (
            <Pressable onPress={handleClear} className="mr-2 p-1">
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </Pressable>
          )}
          <Ionicons name="calendar-outline" size={20} color="#7C3AED" />
        </View>
      </Pressable>

      {/* iOS Modal Picker */}
      {Platform.OS === "ios" && showPicker && (
        <Modal transparent animationType="slide">
          <View className="flex-1 justify-end bg-black/30">
            <View className="bg-white rounded-t-3xl">
              <View className="flex-row justify-between items-center px-4 py-3 border-b border-gray-200">
                <Pressable onPress={handleCancelIOS}>
                  <Text className="text-gray-500 font-medium">Annuleer</Text>
                </Pressable>
                <Text className="text-gray-800 font-semibold">{label}</Text>
                <Pressable onPress={handleConfirmIOS}>
                  <Text className="text-primary-600 font-semibold">Klaar</Text>
                </Pressable>
              </View>
              <DateTimePicker
                value={tempDate}
                mode="date"
                display="spinner"
                onChange={handleChange}
                locale="nl-NL"
                style={{ height: 200 }}
              />
              <View className="h-8" />
            </View>
          </View>
        </Modal>
      )}

      {/* Android Inline Picker */}
      {Platform.OS === "android" && showPicker && (
        <DateTimePicker
          value={tempDate}
          mode="date"
          display="default"
          onChange={handleChange}
        />
      )}
    </View>
  );
};

/**
 * Edit weekend screen
 */
export default function EditWeekendScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const colors = useThemeColors();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [weekend, setWeekend] = useState<FamilyWeekend | null>(null);

  // Form state
  const [year, setYear] = useState("");
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);

  /**
   * Fetch weekend data
   */
  const fetchWeekend = useCallback(async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching weekend:", error);
        Alert.alert("Fout", "Kon weekend niet laden");
        router.back();
        return;
      }

      setWeekend(data);
      setYear(data.year.toString());
      setName(data.name);
      setTheme(data.theme || "");
      setLocationName(data.location_name || "");
      setLocationAddress(data.location_address || "");
      setStartDate(data.start_date ? parseISO(data.start_date) : null);
      setEndDate(data.end_date ? parseISO(data.end_date) : null);
      setIsActive(data.is_active || false);
      setIsFinalized(data.is_finalized || false);
    } catch (error) {
      console.error("Error fetching weekend:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  useEffect(() => {
    fetchWeekend();
  }, [fetchWeekend]);

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    if (!year.trim() || isNaN(Number(year))) {
      Alert.alert("Fout", "Voer een geldig jaar in");
      return false;
    }

    if (!name.trim()) {
      Alert.alert("Fout", "Voer een naam in voor het weekend");
      return false;
    }

    return true;
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActive = async (value: boolean) => {
    if (value) {
      // Activating - show confirmation
      Alert.alert(
        "Weekend Activeren",
        "Weet je zeker dat je dit weekend wilt activeren? Dit deactiveert andere weekenden en trekt familie admin rechten van vorige weekenden in.",
        [
          { text: "Annuleer", style: "cancel" },
          {
            text: "Activeren",
            onPress: async () => {
              try {
                // Deactivate all weekends first
                await supabase
                  .from("family_weekends")
                  .update({ is_active: false })
                  .eq("is_active", true);

                // Activate this weekend
                const { error } = await supabase
                  .from("family_weekends")
                  .update({ is_active: true })
                  .eq("id", id);

                if (error) throw error;

                setIsActive(true);
                setWeekend((prev) => prev ? { ...prev, is_active: true } : prev);

                // Log activation
                if (user && id && name) {
                  logWeekendAction(user.id, id, name, "activate");
                }

                Alert.alert("Succes", "Weekend is geactiveerd");
              } catch (error) {
                console.error("Error activating weekend:", error);
                Alert.alert("Fout", "Kon weekend niet activeren");
              }
            },
          },
        ]
      );
    } else {
      // Deactivating
      Alert.alert(
        "Weekend Deactiveren",
        "Weet je zeker dat je dit weekend wilt deactiveren?",
        [
          { text: "Annuleer", style: "cancel" },
          {
            text: "Deactiveren",
            onPress: async () => {
              try {
                const { error } = await supabase
                  .from("family_weekends")
                  .update({ is_active: false })
                  .eq("id", id);

                if (error) throw error;

                setIsActive(false);
                setWeekend((prev) => prev ? { ...prev, is_active: false } : prev);

                // Log deactivation
                if (user && id && name) {
                  logWeekendAction(user.id, id, name, "deactivate");
                }

                Alert.alert("Succes", "Weekend is gedeactiveerd");
              } catch (error) {
                console.error("Error deactivating weekend:", error);
                Alert.alert("Fout", "Kon weekend niet deactiveren");
              }
            },
          },
        ]
      );
    }
  };

  /**
   * Handle toggle finalized status
   */
  const handleToggleFinalized = async (value: boolean) => {
    const message = value
      ? "Dit weekend wordt zichtbaar in 'Vorige Weekenden' voor alle gebruikers."
      : "Dit weekend wordt niet meer getoond in 'Vorige Weekenden'.";

    Alert.alert(
      value ? "Weekend Afronden" : "Afronding Ongedaan Maken",
      message,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: value ? "Afronden" : "Ongedaan Maken",
          onPress: async () => {
            try {
              const { error } = await supabase
                .from("family_weekends")
                .update({ is_finalized: value })
                .eq("id", id);

              if (error) throw error;

              setIsFinalized(value);
              setWeekend((prev) => prev ? { ...prev, is_finalized: value } : prev);

              // Log finalize/unfinalize action
              if (user && id && name) {
                logWeekendAction(user.id, id, name, value ? "finalize" : "unfinalize");
              }

              Alert.alert(
                "Succes",
                value ? "Weekend is afgerond" : "Afronding is ongedaan gemaakt"
              );
            } catch (error) {
              console.error("Error updating finalized status:", error);
              Alert.alert("Fout", "Kon status niet wijzigen");
            }
          },
        },
      ]
    );
  };

  /**
   * Handle save changes
   */
  const handleSave = async () => {
    if (!validateForm() || !id) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("family_weekends")
        .update({
          year: Number(year),
          name: name.trim(),
          theme: theme.trim() || null,
          location_name: locationName.trim() || null,
          location_address: locationAddress.trim() || null,
          start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
          end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        })
        .eq("id", id);

      if (error) {
        if (error.code === "23505") {
          Alert.alert("Fout", `Er bestaat al een weekend voor ${year}`);
        } else {
          throw error;
        }
        return;
      }

      // Log weekend update
      if (user && id) {
        logWeekendAction(user.id, id, name, "update", {
          year: Number(year),
          theme: theme || null,
          location_name: locationName || null,
        });
      }

      Alert.alert("Succes", "Weekend is bijgewerkt!", [
        {
          text: "OK",
          onPress: () => router.back(),
        },
      ]);
    } catch (error) {
      console.error("Error updating weekend:", error);
      Alert.alert("Fout", "Kon weekend niet bijwerken");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle delete weekend
   */
  const handleDelete = () => {
    if (!weekend) return;

    if (weekend.is_active) {
      Alert.alert("Niet toegestaan", "Je kunt het actieve weekend niet verwijderen.");
      return;
    }

    Alert.alert(
      "Weekend Verwijderen",
      `Weet je zeker dat je "${weekend.name}" wilt verwijderen? Dit verwijdert ook alle bijbehorende teams, scores en data.`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: executeDelete,
        },
      ]
    );
  };

  /**
   * Execute delete
   */
  const executeDelete = async () => {
    if (!id) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("family_weekends")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Log weekend deletion
      if (user && id && weekend) {
        logWeekendAction(user.id, id, weekend.name, "delete");
      }

      Alert.alert("Succes", "Weekend is verwijderd", [
        {
          text: "OK",
          onPress: () => router.back(),
        },
      ]);
    } catch (error) {
      console.error("Error deleting weekend:", error);
      Alert.alert("Fout", "Kon weekend niet verwijderen. Mogelijk zijn er nog gekoppelde gegevens.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#DC2626" />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Weekend laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!weekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="calendar-outline" size={48} color={colors.textTertiary} />
        <Text className="mt-2" style={{ color: colors.textTertiary }}>
          Weekend niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <KeyboardAvoidingView
        className="flex-1"
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
      >
        <ScrollView
          className="flex-1 px-4"
          contentContainerStyle={{ paddingBottom: 40 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Status Section */}
          <View
            className="rounded-xl p-4 mt-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Text
              className="text-sm font-semibold uppercase mb-3"
              style={{ color: colors.textSecondary }}
            >
              Status
            </Text>

            {/* Active Toggle */}
            <View
              className="flex-row items-center justify-between py-3"
              style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
            >
              <View className="flex-1 mr-4">
                <View className="flex-row items-center">
                  <Ionicons name="play-circle-outline" size={20} color="#16A34A" />
                  <Text className="font-medium ml-2" style={{ color: colors.text }}>
                    Actief
                  </Text>
                </View>
                <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  Het huidige weekend waar punten voor gescoord worden
                </Text>
              </View>
              <Switch
                value={isActive}
                onValueChange={handleToggleActive}
                trackColor={{ false: colors.border, true: "#86EFAC" }}
                thumbColor={isActive ? "#16A34A" : colors.textTertiary}
              />
            </View>

            {/* Finalized Toggle */}
            <View className="flex-row items-center justify-between py-3">
              <View className="flex-1 mr-4">
                <View className="flex-row items-center">
                  <Ionicons name="checkmark-done-outline" size={20} color="#7C3AED" />
                  <Text className="font-medium ml-2" style={{ color: colors.text }}>
                    Afgerond
                  </Text>
                </View>
                <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                  Zichtbaar in "Vorige Weekenden" voor alle gebruikers
                </Text>
              </View>
              <Switch
                value={isFinalized}
                onValueChange={handleToggleFinalized}
                trackColor={{ false: colors.border, true: "#C4B5FD" }}
                thumbColor={isFinalized ? "#7C3AED" : colors.textTertiary}
              />
            </View>
          </View>

          {/* Form */}
          <View
            className="rounded-xl p-4 mt-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <InputField
              label="Jaar *"
              value={year}
              onChangeText={setYear}
              placeholder="2026"
              keyboardType="numeric"
              colors={colors}
            />

            <InputField
              label="Naam *"
              value={name}
              onChangeText={setName}
              placeholder="Staatsen Familie Weekend"
              colors={colors}
            />

            <InputField
              label="Thema"
              value={theme}
              onChangeText={setTheme}
              placeholder="Bijv. Beach Party, Winter Wonderland"
              colors={colors}
            />

            <InputField
              label="Locatie naam"
              value={locationName}
              onChangeText={setLocationName}
              placeholder="Bijv. Vakantiepark De Eemhof"
              colors={colors}
            />

            <InputField
              label="Locatie adres"
              value={locationAddress}
              onChangeText={setLocationAddress}
              placeholder="Adres van de locatie"
              multiline
              colors={colors}
            />

            <View className="flex-row">
              <View className="flex-1 mr-2">
                <DateField
                  label="Startdatum"
                  value={startDate}
                  onDateChange={setStartDate}
                  placeholder="Selecteer datum"
                  colors={colors}
                />
              </View>
              <View className="flex-1 ml-2">
                <DateField
                  label="Einddatum"
                  value={endDate}
                  onDateChange={setEndDate}
                  placeholder="Selecteer datum"
                  colors={colors}
                />
              </View>
            </View>
          </View>

          {/* Save Button */}
          <Pressable
            onPress={handleSave}
            disabled={isSaving}
            className="mt-6 rounded-xl py-4 flex-row items-center justify-center"
            style={{
              backgroundColor: isSaving ? "#A78BFA" : "#7C3AED",
            }}
            accessibilityRole="button"
            accessibilityLabel="Wijzigingen opslaan"
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold ml-2">Wijzigingen Opslaan</Text>
              </>
            )}
          </Pressable>

          {/* Delete Button */}
          {!weekend.is_active && (
            <Pressable
              onPress={handleDelete}
              disabled={isSaving}
              className="mt-3 rounded-xl py-4 flex-row items-center justify-center"
              style={{
                backgroundColor: "#FEF2F2",
                borderWidth: 1,
                borderColor: "#FECACA",
              }}
              accessibilityRole="button"
              accessibilityLabel="Weekend verwijderen"
            >
              <Ionicons name="trash-outline" size={20} color="#DC2626" />
              <Text className="font-semibold ml-2" style={{ color: "#DC2626" }}>
                Weekend Verwijderen
              </Text>
            </Pressable>
          )}

          <View className="h-8" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
